import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FloatingAlert } from '../../components/FloatingAlert';
import Loader from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { PeriodDto } from '../../interfaces/period';
import { periodService } from '../../services/PeriodService';
import FirstTimeForm from './FirstTimeForm';

const Home = () => {
    const [periods, setPeriods] = useState<PeriodDto[] | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkPeriods, setCheckPeriods] = useState<boolean>(false);
    const [showForm, setShowForm] = useState<boolean>(false);
    const [showAddPeriod, setShowAddPeriod] = useState<boolean>(false);
    const [statusCode, setStatusCode] = useState<number | null>(null); // Para manejo de estados de respuesta
    const [errorMessage, setErrorMessage] = useState<string | null>(null); // Para manejo de mensajes de error
    const [firstTimeFormDismissed, setFirstTimeFormDismissed] = useState<boolean>(false);
    // FloatingAlert state (single declaration)
    const [showAlerts, setShowAlerts] = useState({
        success: true,
        warning: true,
        danger: true,
        info: true,
    });
    const navigate = useNavigate();
    const { user } = useAuth();
    // Evitar warnings de ESLint - estas variables se usan para control de estado interno
    console.debug('Status:', statusCode, 'Error:', errorMessage);

    const goToCalendar = async () => {
        navigate('/calendario');
    };



    const getUserPeriods = useCallback(async () => {
        setLoading(true);
        setStatusCode(null);
        setErrorMessage(null);

        try {
            if (!user?.id_usuario) {
                throw new Error('No se encontró información del usuario en la sesión');
            }

            // Llamar al servicio para obtener los periodos
            const response = await periodService.getPeriodsByUser({
                id_usuario: user.id_usuario,
            });

            // Siempre setear el statusCode
            setStatusCode(response.statusCode);

            if (response.statusCode === 204) {
                setPeriods([]);
                setShowAddPeriod(true);
                setErrorMessage(response.message ?? null);
                setShowForm(!firstTimeFormDismissed);
                return;
            }

            if (response.statusCode === 200 && Array.isArray(response.periodos)) {
                setPeriods(response.periodos);
                setShowAddPeriod(false);
                setShowForm(false);
                setFirstTimeFormDismissed(false);
                return;
            }

            setErrorMessage(response.message ?? null);
            setPeriods(response.periodos ?? null);
        } catch (err) {
            console.error('Error al obtener periodos:', err);
            setStatusCode(500); // Error genérico
            setErrorMessage('Error interno del servidor');
            setShowForm(false);
        } finally {
            setLoading(false);
        }
    }, [user, firstTimeFormDismissed]);

    useEffect(() => {
        if (user?.id_usuario) {
            getUserPeriods();
        }
    }, [getUserPeriods, user]);

    useEffect(() => {
        if (checkPeriods) {
            getUserPeriods();
            setCheckPeriods(false);
        }
    }, [checkPeriods, getUserPeriods]);

    if (loading) {
        return (
            <Loader text='Cargando...' />
        );
    }

    const handleSkipFirstPeriod = () => {
        setFirstTimeFormDismissed(true);
        setShowForm(false);
        setShowAddPeriod(true);
        setPeriods([]);
    };

    if (showForm) {
        return (
            <FirstTimeForm
                setCheckPeriods={setCheckPeriods}
                setShowForm={setShowForm}
                onSkipFirstPeriod={handleSkipFirstPeriod}
            />
        );
    }

    // Ordenar por fecha de inicio descendente (más reciente primero)
    const sortedPeriods = Array.isArray(periods)
        ? [...periods].sort((a, b) => new Date(b.fecha_inicio).getTime() - new Date(a.fecha_inicio).getTime())
        : [];

    // Lógica homologada para calcular días hasta el próximo periodo
    const getDaysUntilNextPeriod = () => {
        if (sortedPeriods.length === 0) return 0;
        // Ordenar por fecha de inicio ascendente
        const ascPeriods = [...sortedPeriods].sort((a, b) => new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime());
        let cycleLength = 28;
        let lastPeriodStart = new Date(ascPeriods[ascPeriods.length - 1].fecha_inicio.split('T')[0] + 'T00:00:00');
        if (ascPeriods.length >= 2) {
            const prevStart = new Date(ascPeriods[ascPeriods.length - 2].fecha_inicio.split('T')[0] + 'T00:00:00');
            cycleLength = Math.round((lastPeriodStart.getTime() - prevStart.getTime()) / (1000 * 60 * 60 * 24));
        }
        const nextPeriodEstimated = new Date(lastPeriodStart);
        nextPeriodEstimated.setDate(lastPeriodStart.getDate() + cycleLength);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        nextPeriodEstimated.setHours(0, 0, 0, 0);
        const daysUntil = Math.ceil((nextPeriodEstimated.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntil > 0 ? daysUntil : 0;
    };

    // Función para mostrar la fecha estimada del próximo periodo
    // const getNextPeriodDate = () => {
    //     if (sortedPeriods.length === 0) return '-';
    //     const ascPeriods = [...sortedPeriods].sort((a, b) => new Date(a.fecha_inicio).getTime() - new Date(b.fecha_inicio).getTime());
    //     let cycleLength = 28;
    //     let lastPeriodStart = new Date(ascPeriods[ascPeriods.length - 1].fecha_inicio.split('T')[0] + 'T00:00:00');
    //     if (ascPeriods.length >= 2) {
    //         const prevStart = new Date(ascPeriods[ascPeriods.length - 2].fecha_inicio.split('T')[0] + 'T00:00:00');
    //         cycleLength = Math.round((lastPeriodStart.getTime() - prevStart.getTime()) / (1000 * 60 * 60 * 24));
    //     }
    //     const nextPeriodEstimated = new Date(lastPeriodStart);
    //     nextPeriodEstimated.setDate(lastPeriodStart.getDate() + cycleLength);
    //     return nextPeriodEstimated.toLocaleDateString('es-ES', {
    //         day: 'numeric',
    //         month: 'long',
    //         year: 'numeric',
    //     });
    // };



    return (
        <div className='home-container'>
            {/* Ejemplos de FloatingAlert */}
            <div className='floating-alert-container'>
                {showAddPeriod && showAlerts.warning && (
                    <FloatingAlert
                        title='¡Registra tu primer periodo!'
                        message='Para comenzar a usar la app, registra tu primer periodo menstrual con el botón NUEVO PERIODO o en Mi Calendario'
                        mode='warning'
                        onClose={() => setShowAlerts(a => ({ ...a, warning: false }))}
                    />
                )}
            </div>
            {/* Sección de Bienvenida */}
            <div className='welcome-section'>
                <h2 className='welcome-title'>¡Bienvenida{user?.nickname && `, ${user.nickname}`}!</h2>
                <p className='welcome-subtitle'>Gestiona tus periodos y visualiza información importante desde este panel.</p>
            </div>

            {/* Banner Condicional */}
            {showAddPeriod ? (
                // Banner Simple - Sin periodos registrados
                <div className='period-banner'>
                    <h3 className='banner-title'>Gestión de Periodos</h3>
                    <p className='banner-subtitle'>Registra un nuevo periodo para comenzar a trabajar</p>
                    <button className='btn btn-primary' onClick={() => navigate('/calendario')}>
                        Nuevo Periodo
                    </button>
                </div>
            ) : (
                // Banner con Información del Periodo - Con periodos existentes
                <div className='period-info-banner'>
                    <div className='period-info-content'>
                        {/* Sección Izquierda - Último Periodo */}
                        <div className='last-period-section'>
                            <h3 className='period-section-title'>Último Periodo</h3>
                            <div className='period-dates'>
                                <div className='period-date-item'>
                                    <span className='date-label'>Fecha de Inicio:</span>
                                    <span className='date-value'>
                                        {sortedPeriods.length > 0
                                            ? new Date(sortedPeriods[0].fecha_inicio.split('T')[0] + 'T00:00:00').toLocaleDateString('es-ES', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                            })
                                            : '15 de Noviembre, 2024'
                                        }
                                    </span>
                                </div>
                                <div className='period-date-item'>
                                    <span className='date-label'>Fecha de Término:</span>
                                    <span className='date-value'>
                                        {sortedPeriods.length > 0 && sortedPeriods[0].fecha_termino
                                            ? new Date(sortedPeriods[0].fecha_termino!.split('T')[0] + 'T00:00:00').toLocaleDateString('es-ES', {
                                                day: 'numeric',
                                                month: 'long',
                                                year: 'numeric',
                                            })
                                            : sortedPeriods.length > 0
                                                ? 'En curso'
                                                : '20 de Noviembre, 2024'
                                        }
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Sección Derecha - Próximo Periodo y Botón */}
                        <div className='next-period-section'>
                            <div className='next-period-info'>
                                <h4 className='next-period-title'>Próximo Periodo</h4>
                                <div className='countdown-box'>
                                    <div className='countdown-number'>
                                        {sortedPeriods.length > 0 ? getDaysUntilNextPeriod() : 12}
                                    </div>
                                    <div className='countdown-label'>días restantes</div>
                                </div>
                                {/* <div className='next-period-date' style={{ marginTop: 8, fontSize: '1rem', color: '#a21caf' }}>
                                    <span>Fecha estimada: </span>
                                    <span style={{ fontWeight: 500 }}>{getNextPeriodDate()}</span>
                                </div> */}
                            </div>
                            <button className='btn btn-primary' onClick={goToCalendar}>
                                Nuevo Periodo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tres Secciones */}
            <div className='content-grid'>
                {/* Sección 1 - Higiene y Cuidados */}
                <section className='content-card'>
                    <div className='card-icon'>🧼</div>
                    <h4 className='card-title'>Higiene y Cuidados</h4>
                    <p className='card-description'>
                        Consejos esenciales para mantener una higiene adecuada durante tu periodo menstrual.
                    </p>
                    <div className='card-tips'>
                        <div className='tip-item'>
                            <span className='tip-icon'>✨</span>
                            <span>Cambio regular de productos</span>
                        </div>
                        <div className='tip-item'>
                            <span className='tip-icon'>🚿</span>
                            <span>Higiene íntima adecuada</span>
                        </div>
                    </div>
                    <button className='wellbeing-btn wellbeing-btn-secondary' style={{ marginTop: 20 }} onClick={() => navigate('/higiene-y-cuidados')}>
                        Ver más consejos
                    </button>
                </section>

                {/* Sección 2 - Mitos y Verdades */}
                <section className='content-card'>
                    <div className='card-icon'>💭</div>
                    <h4 className='card-title'>Mitos y Verdades sobre mi Periodo</h4>
                    <p className='card-description'>
                        Información confiable para desmentir mitos comunes sobre la menstruación.
                    </p>
                    <div className='myth-truth-container'>
                        <div className='truth-item'>
                            <div className='truth-label'>✓ Verdad</div>
                            <div className='truth-text'>El ejercicio puede aliviar cólicos</div>
                        </div>
                        <div className='myth-item'>
                            <div className='myth-label'>✗ Mito</div>
                            <div className='myth-text'>No puedes nadar durante tu periodo</div>
                        </div>
                    </div>
                    <div className='wellbeing-buttons'>
                        <button className='wellbeing-btn wellbeing-btn-secondary' style={{ marginTop: 20 }} onClick={() => navigate('/mitos-y-verdades')}>
                            Ir a Mitos y Verdades
                        </button>
                    </div>
                </section>

                {/* Sección 3 - Autocuidado y Bienestar */}
                <section className='content-card'>
                    <div className='card-icon'>🌸</div>
                    <h4 className='card-title'>Consejos para el Autocuidado y Bienestar</h4>
                    <p className='card-description'>
                        Prácticas y consejos para cuidar tu bienestar físico y emocional durante tu ciclo menstrual.
                    </p>
                    <div className='wellbeing-buttons'>
                        <button className='wellbeing-btn wellbeing-btn-secondary' onClick={() => navigate('/autocuidado-y-bienestar')}>
                            Ver más consejos
                        </button>
                        {/* <button className='wellbeing-btn wellbeing-btn-secondary'>
                            Rutinas de Bienestar
                        </button> */}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default Home;